import { motion } from "framer-motion";
import {
  Megaphone,
  MousePointerClick,
  MessageCircle,
  ClipboardCheck,
  PhoneCall,
  CalendarCheck,
} from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { WhatsAppButton } from "./CTA";
import { IMAGES } from "../config/images";

const FLOW = [
  { icon: Megaphone, title: "Facebook / Instagram Ad", text: "A targeted campaign reaches the right audience in your market." },
  { icon: MousePointerClick, title: "Landing Page", text: "A focused page presents your packages and captures intent." },
  { icon: MessageCircle, title: "WhatsApp", text: "The enquiry moves into the channel Indian customers actually use." },
  { icon: ClipboardCheck, title: "Qualification", text: "Structured questions filter serious prospects from casual browsers." },
  { icon: PhoneCall, title: "Sales Conversation", text: "Your team speaks with informed, qualified enquiries." },
  { icon: CalendarCheck, title: "Booking Opportunity", text: "Conversations convert into genuine package discussions." },
];

const CHAT = [
  { from: "customer", text: "Assalamu alaikum. Do you have Umrah packages from Mumbai in December?" },
  { from: "agency", text: "Wa alaikum assalam. Yes, we do. May I confirm your dates and group size?" },
  { from: "customer", text: "2 adults and 1 child, around mid-December." },
  { from: "agency", text: "Shukran. Sharing package options. Would a quick call help?" },
];

export const WhatsAppFlow = () => {
  return (
    <section className="bg-white py-24 md:py-32 relative overflow-hidden" data-testid="whatsapp-flow-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <div>
          <Reveal>
            <SectionTag className="mb-6">{"WhatsApp-First"}</SectionTag>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-charcoal leading-[1.05]">
              {"Turn Interest Into Real Conversations."}
            </h2>
            <p className="mt-6 text-base text-charcoal/65 leading-relaxed max-w-xl">
              {"For many Indian Hajj & Umrah customers, the first real conversation happens on WhatsApp, not on a website form. Nexus Catalyst builds WhatsApp directly into the acquisition journey, so enquiries land where your team can respond fast."}
            </p>
          </Reveal>
          <div className="mt-10 relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-charcoal/10" />
            <div className="space-y-6">
              {FLOW.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.06}>
                  <div className="flex items-start gap-5" data-testid={`whatsapp-flow-step-${i + 1}`}>
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center bg-cream border border-gold/40">
                      <step.icon size={17} strokeWidth={1.5} className="text-tan" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-charcoal">
                        {step.title}
                      </div>
                      <div className="text-sm text-charcoal/55 mt-1 leading-relaxed">
                        {step.text}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2} className="mt-10">
            <WhatsAppButton
              label="Improve My WhatsApp Lead Flow"
              testid="whatsapp-flow-cta"
            />
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-full h-full border border-gold/40" />
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.smartphone}
                alt="Smartphone ready for a WhatsApp conversation"
                className="w-full h-[420px] md:h-[520px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-charcoal/45" />
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="w-full max-w-[320px] bg-cream shadow-[0_24px_60px_rgba(0,0,0,0.35)]" dir="ltr">
                  <div className="flex items-center gap-3 bg-charcoal px-4 py-3">
                    <span className="h-2.5 w-2.5 rotate-45 bg-gold" />
                    <div>
                      <div className="text-cream text-xs font-semibold">{"Your Agency"}</div>
                      <div className="text-cream/50 text-[10px]">{"WhatsApp Business"}</div>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    {CHAT.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.35, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed ${
                          msg.from === "agency"
                            ? "ml-auto bg-tan text-cream"
                            : "bg-white text-charcoal border border-charcoal/10"
                        }`}
                      >
                        {msg.text}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-5 text-xs text-charcoal/45 max-w-sm">
            {"Illustrative first-response flow, structured around your agency's actual sales process."}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
